import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { NoteIcon } from '../../assets';
import {
  CREATE_BRAND_NOTE,
  UPDATE_BRAND_NOTE,
} from '../../graphql/brandActivity/mutation';
import { useNotification } from '../../hooks/useNotification';
import { Button } from '../common/Button';
import { Error } from '../common/Error';
import { PopupLayout } from '../popups/PopupLayout';

type BrandNotePopupProps = {
  brandId: string;
  onClose: () => void;
  onSaved: () => void;
  noteId?: string;
  defaultMessage?: string;
};

type TNoteForm = {
  message: string;
};

export const CreateBrandNote = ({
  brandId,
  onClose,
  onSaved,
  noteId,
  defaultMessage,
}: BrandNotePopupProps) => {
  const { t } = useTranslation('common');
  const { notifySaveSuccess, notifySaveError } = useNotification();
  const isEdit = !!noteId;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TNoteForm>({
    defaultValues: { message: defaultMessage || '' },
  });

  const [createNote, { loading: loadingCreate }] =
    useMutation(CREATE_BRAND_NOTE);
  const [updateNote, { loading: loadingUpdate }] =
    useMutation(UPDATE_BRAND_NOTE);

  const onSubmit = (data: TNoteForm) => {
    if (isEdit) {
      updateNote({
        variables: { noteId, message: data.message },
        onCompleted: () => {
          notifySaveSuccess();
          onSaved();
        },
        onError: () => notifySaveError(),
      });
    } else {
      createNote({
        variables: { brandId, message: data.message },
        onCompleted: () => {
          notifySaveSuccess();
          onSaved();
        },
        onError: () => notifySaveError(),
      });
    }
  };

  return (
    <PopupLayout
      title={
        isEdit ? t('activity.editNoteTitle') : t('activity.createNoteTitle')
      }
      closeModal={onClose}
      icon={<NoteIcon className="size-[20px]" />}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4 mt-4"
      >
        <div className="w-full">
          <label className="my-2 block text-[14px] font-[600]">
            {t('activity.noteMessage')}
          </label>
          <textarea
            id="brandNoteTextArea"
            className={`w-full h-[100px] px-[5px] py-[2px] rounded-[5px] border-none outline-none text-sm text-dark-blue ${errors.message ? 'border border-red-600' : ''}`}
            placeholder={t('activity.notePlaceholder')}
            {...register('message', { required: t('global.requiredField') })}
          />
          <div className="mt-1">
            {errors.message?.message && (
              <Error error={errors.message.message} />
            )}
          </div>
        </div>
        <div className="flex justify-end">
          <Button
            text={t('global.save')}
            style="light-blue"
            type="submit"
            loading={loadingCreate || loadingUpdate}
            customClassnames="!px-14"
          />
        </div>
      </form>
    </PopupLayout>
  );
};
